import { connect } from 'react-redux'
import { IObject } from 'interfaces'
import { fetch } from 'ducks/data'
import { getDependencies } from 'utils/dependencies'
import { isDependencyForObject } from 'utils/dataBindings'
import { ContextConsumerComponent } from 'components/ContextConsumerComponent'

const DEFAULT_PAGE_SIZE = 20
const PREFETCH_SECOND_PAGE = true

const mapStateToProps = (state: any) => ({ globalState: state })

export const connectPaginatedFetch = connect(mapStateToProps, { fetch })

export type PaginatedFetchProps = {
  component: IObject
  object: IObject
  globalState: any
  fetch: undefined | ((...args: any[]) => Promise<any>)
}

class PaginatedFetchComponent<
  P extends PaginatedFetchProps,
  S = {}
> extends ContextConsumerComponent<P, S> {
  getPageSize() {
    return DEFAULT_PAGE_SIZE
  }

  isManuallyPaginated() {
    const { object, component, globalState } = this.props
    const { getApp, getDatasources, getParams } = this.context
    const deps = getDependencies(component, {
      getApp,
      getDatasources,
      getParams,
      state: globalState,
    })

    const dependencyForTable = deps.find(dep =>
      isDependencyForObject(object.id, dep)
    )

    // External collections data is not manually paginated.
    // It should still be loaded and handled by the Screen, same as Lists

    const isExternalCollection =
      dependencyForTable?.isExternalCollection === true

    return !isExternalCollection
  }

  componentDidMount() {
    if (!this.isManuallyPaginated()) {
      return
    }

    const pageSize = this.getPageSize()

    this.fetchPageData(0, pageSize).then(() => {
      if (PREFETCH_SECOND_PAGE) {
        this.fetchPageData(1, pageSize)
      }
    })
  }

  fetchPageData(page: number, limit = DEFAULT_PAGE_SIZE) {
    if (!this.isManuallyPaginated()) {
      // No manual fetching if the data is not manually paginated
      return Promise.resolve()
    }

    const { component, globalState, fetch } = this.props
    const { getBaseURL, getApp, getDatasources, getParams } = this.context

    if (!fetch) {
      throw new Error(
        `PaginatedFetchComponent is missing 'fetch' prop, make sure to use connectPaginatedFetch HOC`
      )
    }

    const url = getBaseURL()
    const dependencies = getDependencies(component, {
      getDatasources,
      getApp,
      state: globalState,
      getParams,
    })

    const paginationOptions = { offset: page * limit, limit }

    return Promise.all(
      dependencies.map(dep => fetch(url, { ...dep, ...paginationOptions }))
    )
  }
}

export default PaginatedFetchComponent
